import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import { asHeading4 } from './productDetailTypography';
import type { VariantBarcodesEditor } from './useVariantBarcodesForm';
import { VariantBarcodeAlternate } from './VariantBarcodeAlternate';

type VariantBarcodesAlternatesProps = {
    editor: VariantBarcodesEditor;
};

/**
 * The drawer's alternate barcodes, under their own heading: one
 * `VariantBarcodeAlternate` each, or a line saying there is none.
 */
export const VariantBarcodesAlternates: React.FC<
    VariantBarcodesAlternatesProps
> = ({ editor }) => {
    const { t } = useTranslation('pages');
    const { alternates } = editor.draft;

    return (
        <section
            aria-labelledby="variant-barcodes-alternates-title"
            className="flex flex-col gap-3"
        >
            <Typography
                id="variant-barcodes-alternates-title"
                render={asHeading4}
                type="body-sm"
                weight="medium"
            >
                {t('products.detail.barcodes.alternatesLabel')}
            </Typography>
            {alternates.length === 0 ? (
                <Typography
                    color="muted"
                    type="body-sm"
                    {...getNodeTestIdProp('variant-barcodes-none')}
                >
                    {t('products.detail.barcodes.none')}
                </Typography>
            ) : (
                <ul className="flex flex-col gap-3">
                    {alternates.map((alternate) => (
                        <VariantBarcodeAlternate
                            alternate={alternate}
                            editor={editor}
                            key={alternate.key}
                        />
                    ))}
                </ul>
            )}
        </section>
    );
};
